import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "./budgets.css";

const emptyScenarioForm = () => ({
  name: "",
  description: "",
  scenario_type: "base",
  source_budget_version_id: "",
  source_forecast_version_id: "",
});

const emptyAssumptionForm = () => ({
  assumption_type: "account_adjustment",
  target_type: "account",
  target_ref_id: "",
  operator: "increase_by_pct",
  assumption_value: 0,
  period_scope: "all_periods",
  month_start: "",
  month_end: "",
  notes: "",
});

function currency(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function monthShortLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleString("en-US", { month: "short" });
}

function buildScenarioMatrixRows(results = []) {
  const map = new Map();

  results.forEach((row, index) => {
    const key = row.account_id || `row-${index}`;

    if (!map.has(key)) {
      map.set(key, {
        id: key,
        label: `${row.account_number || ""} - ${row.account_name || "Unknown"}`,
        subLabel: row.account_category || "",
        group: row.account_type || "Other",
        category: row.account_category || "Other",
        months: [],
      });
    }

    map.get(key).months.push({
      month_key: row.period_key,
      month_label: row.period_label,
      base_amount: Number(row.base_amount) || 0,
      scenario_amount: Number(row.scenario_amount) || 0,
      variance_amount: Number(row.variance_amount) || 0,
      variance_pct:
        row.variance_pct === null || row.variance_pct === undefined
          ? null
          : Number(row.variance_pct),
    });
  });

  return Array.from(map.values());
}

function EditAssumptionModal({ open, onClose, assumption, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && assumption) {
      setForm({
        ...assumption,
        assumption_value: assumption.assumption_value || 0,
      });
      setErr("");
    }
  }, [open, assumption]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setErr("");

      const { error } = await supabase
        .from("planning_scenario_assumptions")
        .update({
          operator: form.operator,
          assumption_value: Number(form.assumption_value),
          period_scope: form.period_scope,
          month_start: form.month_start || null,
          month_end: form.month_end || null,
          notes: form.notes || null,
        })
        .eq("id", assumption.id);

      if (error) throw error;

      await onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !form) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <h3>Edit Assumption</h3>
          <button onClick={onClose}>×</button>
        </div>

        <div className="kp-form">
          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Operator</label>
              <select
                value={form.operator}
                onChange={(e) =>
                  setForm((p) => ({ ...p, operator: e.target.value }))
                }
              >
                <option value="set">set</option>
                <option value="increase_by_pct">increase_by_pct</option>
                <option value="decrease_by_pct">decrease_by_pct</option>
                <option value="increase_by_value">increase_by_value</option>
                <option value="decrease_by_value">decrease_by_value</option>
                <option value="multiply_by">multiply_by</option>
              </select>
            </div>

            <div className="kp-field">
              <label>Value</label>
              <input
                type="number"
                value={form.assumption_value}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    assumption_value: e.target.value,
                  }))
                }
              />
            </div>

            <div className="kp-field">
              <label>Period Scope</label>
              <select
                value={form.period_scope}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    period_scope: e.target.value,
                  }))
                }
              >
                <option value="all_periods">all_periods</option>
                <option value="range">range</option>
              </select>
            </div>

            {form.period_scope === "range" && (
              <>
                <div className="kp-field">
                  <label>Start</label>
                  <input
                    type="date"
                    value={form.month_start || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        month_start: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="kp-field">
                  <label>End</label>
                  <input
                    type="date"
                    value={form.month_end || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        month_end: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            )}
          </div>

          <div className="kp-field">
            <label>Notes</label>
            <input
              value={form.notes || ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, notes: e.target.value }))
              }
            />
          </div>

          {err && <div className="kp-alert kp-alert-error">{err}</div>}

          <div className="kp-modal-footer">
            <button onClick={onClose} className="kp-btn kp-btn-soft">
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="kp-btn kp-btn-primary"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateScenarioModal({
  open,
  onClose,
  onCreated,
  companyId,
  sessionUserId,
  selectedVersionId,
  forecastVersions,
}) {
  const [form, setForm] = useState(emptyScenarioForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        ...emptyScenarioForm(),
        source_budget_version_id: selectedVersionId || "",
      });
      setSaving(false);
      setErr("");
    }
  }, [open, selectedVersionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    try {
      setSaving(true);

      const { data, error } = await supabase
        .from("planning_scenarios")
        .insert({
          company_id: companyId,
          name: form.name.trim(),
          description: form.description.trim() || null,
          scenario_type: form.scenario_type,
          source_budget_version_id: form.source_budget_version_id || null,
          source_forecast_version_id: form.source_forecast_version_id || null,
          status: "draft",
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      await onCreated?.(data);
      onClose?.();
    } catch (error) {
      console.error("Create scenario error:", error);
      setErr(error.message || "Failed to create scenario.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="kp-modal-overlay" onClick={onClose}>
      <div
        className="kp-modal kp-modal-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kp-modal-header">
          <div>
            <h3>Create Scenario</h3>
            <p>Create a planning scenario for comparison and analysis.</p>
          </div>
          <button
            type="button"
            className="kp-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="kp-form" onSubmit={handleSubmit}>
          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Type</label>
              <select
                value={form.scenario_type}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    scenario_type: e.target.value,
                  }))
                }
              >
                <option value="base">base</option>
                <option value="best">best</option>
                <option value="worst">worst</option>
                <option value="growth">growth</option>
                <option value="cost_control">cost_control</option>
                <option value="custom">custom</option>
              </select>
            </div>

            <div className="kp-field">
              <label>Source Budget Version</label>
              <input
                value={form.source_budget_version_id}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    source_budget_version_id: e.target.value,
                  }))
                }
                placeholder="Optional budget version id"
              />
            </div>

            <div className="kp-field">
              <label>Source Forecast Version</label>
              <select
                value={form.source_forecast_version_id}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    source_forecast_version_id: e.target.value,
                  }))
                }
              >
                <option value="">None</option>
                {forecastVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.version_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="kp-field">
            <label>Description</label>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </div>

          {err && <div className="kp-alert kp-alert-error">{err}</div>}

          <div className="kp-modal-footer">
            <button
              type="button"
              className="kp-btn kp-btn-soft"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn-primary"
              disabled={saving}
            >
              {saving ? "Creating..." : "Create Scenario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlanningScenarioMatrixTable({
  title,
  rows = [],
  collapsedGroups = {},
  onToggleGroup,
}) {
  const monthColumns = useMemo(() => {
    const months = Array.from(
      new Set(
        rows.flatMap((r) =>
          Array.isArray(r.months) ? r.months.map((m) => m.month_key) : []
        )
      )
    ).sort();

    return months.map((m) => ({
      key: m,
      label: monthShortLabel(m),
    }));
  }, [rows]);

  const grouped = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = row.group || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    return Array.from(map.entries()).map(([group, items]) => ({
      group,
      rows: items,
    }));
  }, [rows]);

  return (
    <div className="kp-card">
      <div className="kp-card-head">
        <h3>{title}</h3>
      </div>

      <div className="kp-table-wrap kp-planning-matrix-wrap">
        <table className="kp-table kp-planning-matrix">
          <thead>
            <tr>
              <th className="sticky-col first-col sticky-head">Line Item</th>
              {monthColumns.map((col) => (
                <th key={col.key} className="sticky-head">
                  {col.label}
                </th>
              ))}
              <th className="sticky-head">Year</th>
            </tr>
          </thead>

          <tbody>
            {grouped.length === 0 ? (
              <tr>
                <td colSpan={monthColumns.length + 2} className="kp-empty-cell">
                  No scenario rows available.
                </td>
              </tr>
            ) : (
              grouped.map((section) => {
                const isCollapsed = !!collapsedGroups[section.group];

                const sectionBaseTotals = {};
                const sectionScenarioTotals = {};
                const sectionVarianceTotals = {};
                monthColumns.forEach((col) => {
                  sectionBaseTotals[col.key] = 0;
                  sectionScenarioTotals[col.key] = 0;
                  sectionVarianceTotals[col.key] = 0;
                });

                let sectionBaseYear = 0;
                let sectionScenarioYear = 0;
                let sectionVarianceYear = 0;

                section.rows.forEach((row) => {
                  monthColumns.forEach((col) => {
                    const found = (row.months || []).find(
                      (m) => m.month_key === col.key
                    );
                    sectionBaseTotals[col.key] += Number(
                      found?.base_amount || 0
                    );
                    sectionScenarioTotals[col.key] += Number(
                      found?.scenario_amount || 0
                    );
                    sectionVarianceTotals[col.key] += Number(
                      found?.variance_amount || 0
                    );
                  });

                  sectionBaseYear += (row.months || []).reduce(
                    (sum, m) => sum + (Number(m.base_amount) || 0),
                    0
                  );
                  sectionScenarioYear += (row.months || []).reduce(
                    (sum, m) => sum + (Number(m.scenario_amount) || 0),
                    0
                  );
                  sectionVarianceYear += (row.months || []).reduce(
                    (sum, m) => sum + (Number(m.variance_amount) || 0),
                    0
                  );
                });

                return (
                  <React.Fragment key={section.group}>
                    <tr
                      className="kp-section-row kp-section-clickable"
                      onClick={() => onToggleGroup?.(section.group)}
                    >
                      <td
                        colSpan={monthColumns.length + 2}
                        className="sticky-col first-col"
                      >
                        <span className="kp-section-toggle">
                          {isCollapsed ? "▸" : "▾"}
                        </span>{" "}
                        {section.group}
                      </td>
                    </tr>

                    {!isCollapsed &&
                      section.rows.map((row) => {
                        const baseYear = (row.months || []).reduce(
                          (sum, m) => sum + (Number(m.base_amount) || 0),
                          0
                        );
                        const scenarioYear = (row.months || []).reduce(
                          (sum, m) => sum + (Number(m.scenario_amount) || 0),
                          0
                        );
                        const varianceYear = (row.months || []).reduce(
                          (sum, m) => sum + (Number(m.variance_amount) || 0),
                          0
                        );

                        return (
                          <tr key={row.id}>
                            <td className="sticky-col first-col">
                              <div className="kp-line-main">
                                <strong>{row.label}</strong>
                                {row.subLabel ? (
                                  <small>{row.subLabel}</small>
                                ) : null}
                              </div>
                            </td>

                            {monthColumns.map((col) => {
                              const found = (row.months || []).find(
                                (m) => m.month_key === col.key
                              );

                              return (
                                <td key={col.key}>
                                  <div className="kp-scenario-cell">
                                    <div>
                                      B: {currency(found?.base_amount || 0)}
                                    </div>
                                    <div>
                                      S: {currency(found?.scenario_amount || 0)}
                                    </div>
                                    <div
                                      className={
                                        Number(found?.variance_amount || 0) >= 0
                                          ? "pos"
                                          : "neg"
                                      }
                                    >
                                      V: {currency(found?.variance_amount || 0)}
                                    </div>
                                  </div>
                                </td>
                              );
                            })}

                            <td>
                              <div className="kp-scenario-cell">
                                <div>B: {currency(baseYear)}</div>
                                <div>S: {currency(scenarioYear)}</div>
                                <div
                                  className={varianceYear >= 0 ? "pos" : "neg"}
                                >
                                  V: {currency(varianceYear)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                    {!isCollapsed && (
                      <tr className="kp-subtotal-row">
                        <td className="sticky-col first-col">Subtotal</td>
                        {monthColumns.map((col) => (
                          <td key={col.key}>
                            <div className="kp-scenario-cell">
                              <div>
                                B: {currency(sectionBaseTotals[col.key])}
                              </div>
                              <div>
                                S: {currency(sectionScenarioTotals[col.key])}
                              </div>
                              <div
                                className={
                                  sectionVarianceTotals[col.key] >= 0
                                    ? "pos"
                                    : "neg"
                                }
                              >
                                V: {currency(sectionVarianceTotals[col.key])}
                              </div>
                            </div>
                          </td>
                        ))}
                        <td>
                          <div className="kp-scenario-cell">
                            <div>B: {currency(sectionBaseYear)}</div>
                            <div>S: {currency(sectionScenarioYear)}</div>
                            <div
                              className={
                                sectionVarianceYear >= 0 ? "pos" : "neg"
                              }
                            >
                              V: {currency(sectionVarianceYear)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ScenarioManager({
  budgets = [],
  selectedBudgetId = "",
  selectedVersionId = "",
}) {
  const { companyId } = useCompany();

  const [sessionUserId, setSessionUserId] = useState("");
  const [role, setRole] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const [assumptions, setAssumptions] = useState([]);
  const [results, setResults] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [forecastVersions, setForecastVersions] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assumptionForm, setAssumptionForm] = useState(emptyAssumptionForm());
  const [editingAssumption, setEditingAssumption] = useState(null);

  const [compareFilters, setCompareFilters] = useState({
    start_date: "",
    end_date: "",
    granularity: "monthly",
  });

  const [collapsedGroups, setCollapsedGroups] = useState({});

  const scenarioMatrixRows = useMemo(
    () => buildScenarioMatrixRows(results),
    [results]
  );

  const totals = useMemo(() => {
    return results.reduce(
      (acc, row) => {
        acc.base += Number(row.base_amount) || 0;
        acc.scenario += Number(row.scenario_amount) || 0;
        acc.variance += Number(row.variance_amount) || 0;
        return acc;
      },
      { base: 0, scenario: 0, variance: 0 }
    );
  }, [results]);

  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === selectedScenarioId) || null,
    [scenarios, selectedScenarioId]
  );

  useEffect(() => {
    const selectedBudget = budgets.find((b) => b.id === selectedBudgetId);
    if (selectedBudget) {
      setCompareFilters((prev) => ({
        ...prev,
        start_date: prev.start_date || selectedBudget.start_date,
        end_date: prev.end_date || selectedBudget.end_date,
      }));
    }
  }, [budgets, selectedBudgetId]);

  const loadSessionAndRole = useCallback(async () => {
    if (!companyId) return;

    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr) throw authErr;
    if (!user?.id) throw new Error("User session not found.");

    setSessionUserId(user.id);

    const { data: member, error: memberErr } = await supabase
      .from("company_members")
      .select("role, accepted")
      .eq("company_id", companyId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (memberErr) throw memberErr;

    setRole(String(member?.role || "member").toLowerCase());
  }, [companyId]);

  const loadScenarios = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("planning_scenarios")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    setScenarios(data || []);
    if (!selectedScenarioId && data?.length) {
      setSelectedScenarioId(data[0].id);
    }
  }, [companyId, selectedScenarioId]);

  const loadAssumptions = useCallback(async () => {
    if (!selectedScenarioId) {
      setAssumptions([]);
      return;
    }

    const { data, error } = await supabase
      .from("planning_scenario_assumptions")
      .select("*")
      .eq("scenario_id", selectedScenarioId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    setAssumptions(data || []);
  }, [selectedScenarioId]);

  const loadAccountsAndForecastVersions = useCallback(async () => {
    if (!companyId) return;

    const [
      { data: accountRows, error: accountErr },
      { data: forecastRows, error: forecastErr },
    ] = await Promise.all([
      supabase.rpc("get_budget_eligible_accounts", { p_company_id: companyId }),
      supabase
        .from("planning_forecast_versions")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false }),
    ]);

    if (accountErr) throw accountErr;
    if (forecastErr) throw forecastErr;

    setAccounts(accountRows || []);
    setForecastVersions(forecastRows || []);
  }, [companyId]);

  const loadScenarioResults = useCallback(async () => {
    if (
      !selectedScenarioId ||
      !compareFilters.start_date ||
      !compareFilters.end_date
    ) {
      setResults([]);
      return;
    }

    const { data, error } = await supabase.rpc(
      "get_scenario_comparison_report",
      {
        p_scenario_id: selectedScenarioId,
        p_start_date: compareFilters.start_date,
        p_end_date: compareFilters.end_date,
        p_granularity: compareFilters.granularity,
      }
    );

    if (error) throw error;
    setResults(data || []);
  }, [selectedScenarioId, compareFilters]);

  const initialize = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setErr("");
      await Promise.all([
        loadSessionAndRole(),
        loadScenarios(),
        loadAccountsAndForecastVersions(),
      ]);
    } catch (error) {
      console.error("Scenario workspace load error:", error);
      setErr(error.message || "Failed to load scenarios.");
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    loadAccountsAndForecastVersions,
    loadScenarios,
    loadSessionAndRole,
  ]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    loadAssumptions().catch((error) => {
      console.error("Load assumptions error:", error);
      setErr(error.message || "Failed to load assumptions.");
    });
  }, [loadAssumptions]);

  useEffect(() => {
    loadScenarioResults().catch((error) => {
      console.error("Load scenario results error:", error);
      setErr(error.message || "Failed to load scenario comparison.");
    });
  }, [loadScenarioResults]);

  const handleCreated = async (scenarioRow) => {
    await loadScenarios();
    if (scenarioRow?.id) {
      setSelectedScenarioId(scenarioRow.id);
    }
  };

  const addAssumption = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      const { error } = await supabase
        .from("planning_scenario_assumptions")
        .insert({
          company_id: companyId,
          scenario_id: selectedScenarioId,
          assumption_type: assumptionForm.assumption_type,
          target_type: assumptionForm.target_type,
          target_ref_id: assumptionForm.target_ref_id || null,
          operator: assumptionForm.operator,
          assumption_value: Number(assumptionForm.assumption_value || 0),
          period_scope: assumptionForm.period_scope,
          month_start: assumptionForm.month_start || null,
          month_end: assumptionForm.month_end || null,
          notes: assumptionForm.notes.trim() || null,
          created_by: sessionUserId,
        });

      if (error) throw error;

      setAssumptionForm(emptyAssumptionForm());
      setSuccess("Scenario assumption added successfully.");
      await loadAssumptions();
    } catch (error) {
      console.error("Add scenario assumption error:", error);
      setErr(error.message || "Failed to add assumption.");
    } finally {
      setSaving(false);
    }
  };

  const recalculateScenario = async () => {
    setErr("");
    setSuccess("");

    try {
      setSaving(true);

      const { error } = await supabase.rpc("recalculate_scenario", {
        p_scenario_id: selectedScenarioId,
      });

      if (error) throw error;

      setSuccess("Scenario recalculated successfully.");
      await loadScenarioResults();
    } catch (error) {
      console.error("Recalculate scenario error:", error);
      setErr(error.message || "Failed to recalculate scenario.");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const exportToExcel = () => {
    const monthKeys = Array.from(
      new Set(
        scenarioMatrixRows.flatMap((row) =>
          (row.months || []).map((m) => m.month_key)
        )
      )
    ).sort();

    const rows = scenarioMatrixRows.map((row) => {
      const item = {
        Line_Item: row.label,
        Details: row.subLabel || "",
        Group: row.group,
      };

      monthKeys.forEach((monthKey) => {
        const found = (row.months || []).find((m) => m.month_key === monthKey);
        item[`${monthShortLabel(monthKey)}_Base`] = Number(
          found?.base_amount || 0
        );
        item[`${monthShortLabel(monthKey)}_Scenario`] = Number(
          found?.scenario_amount || 0
        );
        item[`${monthShortLabel(monthKey)}_Variance`] = Number(
          found?.variance_amount || 0
        );
      });

      item.Base_Year = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.base_amount) || 0),
        0
      );
      item.Scenario_Year = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.scenario_amount) || 0),
        0
      );
      item.Variance_Year = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.variance_amount) || 0),
        0
      );

      return item;
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Scenario Matrix");
    XLSX.writeFile(
      workbook,
      `${(selectedScenario?.name || "scenario").replace(
        /[^a-z0-9]/gi,
        "_"
      )}_matrix.xlsx`
    );
  };

  const deleteAssumption = async (id) => {
    if (!window.confirm("Delete this assumption?")) return;

    try {
      const { error } = await supabase
        .from("planning_scenario_assumptions")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await loadAssumptions();
      await loadScenarioResults();
    } catch (e) {
      setErr(e.message);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    const monthKeys = Array.from(
      new Set(
        scenarioMatrixRows.flatMap((row) =>
          (row.months || []).map((m) => m.month_key)
        )
      )
    ).sort();

    const head = [
      [
        "Line Item",
        ...monthKeys.map((monthKey) => monthShortLabel(monthKey)),
        "Year",
      ],
    ];

    const body = scenarioMatrixRows.map((row) => {
      const yearVariance = (row.months || []).reduce(
        (sum, m) => sum + (Number(m.variance_amount) || 0),
        0
      );

      return [
        row.label,
        ...monthKeys.map((monthKey) => {
          const found = (row.months || []).find(
            (m) => m.month_key === monthKey
          );
          return `${Number(
            found?.scenario_amount || 0
          ).toLocaleString()} (${Number(
            found?.variance_amount || 0
          ).toLocaleString()})`;
        }),
        yearVariance.toLocaleString(),
      ];
    });

    doc.setFontSize(14);
    doc.text(selectedScenario?.name || "Scenario Matrix", 14, 14);

    autoTable(doc, {
      head,
      body,
      startY: 20,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39] },
      margin: { left: 8, right: 8 },
    });

    doc.save(
      `${(selectedScenario?.name || "scenario").replace(
        /[^a-z0-9]/gi,
        "_"
      )}_matrix.pdf`
    );
  };

  if (loading) {
    return <div className="kp-card">Loading scenarios…</div>;
  }

  return (
    <div className="kp-budget-page">
      <div className="kp-budget-topbar">
        <div>
          <h2>Scenario Workspace</h2>
          <p>
            Scenario planning and comparison · Role:{" "}
            <strong>{role || "member"}</strong>
          </p>
        </div>

        <div className="kp-inline-actions">
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={exportToExcel}
          >
            Export Excel
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-soft"
            onClick={exportToPDF}
          >
            Export PDF
          </button>
          <button
            type="button"
            className="kp-btn kp-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create Scenario
          </button>
        </div>
      </div>

      {err && <div className="kp-alert kp-alert-error">{err}</div>}
      {success && <div className="kp-alert kp-alert-success">{success}</div>}

      <div className="kp-grid-2">
        <div className="kp-card">
          <div className="kp-card-head">
            <h3>Scenarios</h3>
          </div>

          <div className="kp-budget-list">
            {scenarios.length === 0 ? (
              <div className="kp-empty">No scenarios yet.</div>
            ) : (
              scenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  className={`kp-budget-item ${
                    selectedScenarioId === scenario.id ? "selected" : ""
                  }`}
                  onClick={() => setSelectedScenarioId(scenario.id)}
                >
                  <div className="kp-budget-item-top">
                    <strong>{scenario.name}</strong>
                    <span className="kp-badge status-draft">
                      {scenario.status || "draft"}
                    </span>
                  </div>
                  <div className="kp-budget-item-sub">
                    {scenario.scenario_type}
                    {scenario.source_forecast_version_id
                      ? " · forecast-linked"
                      : ""}
                    {scenario.source_budget_version_id
                      ? " · budget-linked"
                      : ""}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="kp-card">
          <div className="kp-card-head">
            <h3>Scenario Assumptions</h3>
            {!!selectedScenarioId && (
              <button
                type="button"
                className="kp-btn kp-btn-soft"
                onClick={recalculateScenario}
                disabled={saving}
              >
                {saving ? "Working..." : "Recalculate"}
              </button>
            )}
          </div>

          {!selectedScenarioId ? (
            <div className="kp-note">Select a scenario first.</div>
          ) : (
            <>
              <form className="kp-form" onSubmit={addAssumption}>
                <div className="kp-form-grid kp-form-grid-4">
                  <div className="kp-field">
                    <label>Assumption Type</label>
                    <select
                      value={assumptionForm.assumption_type}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          assumption_type: e.target.value,
                        }))
                      }
                    >
                      <option value="driver_value">driver_value</option>
                      <option value="growth_rate">growth_rate</option>
                      <option value="account_adjustment">
                        account_adjustment
                      </option>
                      <option value="percentage_uplift">
                        percentage_uplift
                      </option>
                      <option value="percentage_reduction">
                        percentage_reduction
                      </option>
                      <option value="timing_shift">timing_shift</option>
                    </select>
                  </div>

                  <div className="kp-field">
                    <label>Target Type</label>
                    <select
                      value={assumptionForm.target_type}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          target_type: e.target.value,
                        }))
                      }
                    >
                      <option value="account">account</option>
                      <option value="driver">driver</option>
                      <option value="formula_group">formula_group</option>
                      <option value="category">category</option>
                    </select>
                  </div>

                  <div className="kp-field">
                    <label>Target</label>
                    <select
                      value={assumptionForm.target_ref_id}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          target_ref_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select target</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.account_number} - {account.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="kp-field">
                    <label>Operator</label>
                    <select
                      value={assumptionForm.operator}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          operator: e.target.value,
                        }))
                      }
                    >
                      <option value="set">set</option>
                      <option value="increase_by_pct">increase_by_pct</option>
                      <option value="decrease_by_pct">decrease_by_pct</option>
                      <option value="increase_by_value">
                        increase_by_value
                      </option>
                      <option value="decrease_by_value">
                        decrease_by_value
                      </option>
                      <option value="multiply_by">multiply_by</option>
                    </select>
                  </div>
                </div>

                <div className="kp-form-grid">
                  <div className="kp-field">
                    <label>Value</label>
                    <input
                      type="number"
                      value={assumptionForm.assumption_value}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          assumption_value: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="kp-field">
                    <label>Period Scope</label>
                    <select
                      value={assumptionForm.period_scope}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          period_scope: e.target.value,
                        }))
                      }
                    >
                      <option value="all_periods">all_periods</option>
                      <option value="specific_periods">specific_periods</option>
                      <option value="range">range</option>
                    </select>
                  </div>

                  <div className="kp-field">
                    <label>Month Start</label>
                    <input
                      type="date"
                      value={assumptionForm.month_start}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          month_start: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="kp-field">
                    <label>Month End</label>
                    <input
                      type="date"
                      value={assumptionForm.month_end}
                      onChange={(e) =>
                        setAssumptionForm((prev) => ({
                          ...prev,
                          month_end: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="kp-field">
                  <label>Notes</label>
                  <input
                    value={assumptionForm.notes}
                    onChange={(e) =>
                      setAssumptionForm((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                  />
                </div>

                <button className="kp-btn kp-btn-primary" disabled={saving}>
                  Add Assumption
                </button>
              </form>

              <div className="kp-table-wrap" style={{ marginTop: 16 }}>
                <table className="kp-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Target Type</th>
                      <th>Operator</th>
                      <th>Value</th>
                      <th>Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assumptions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="kp-empty-cell">
                          No assumptions yet.
                        </td>
                      </tr>
                    ) : (
                      assumptions.map((item) => (
                        <tr key={item.id}>
                          <td>{item.assumption_type}</td>
                          <td>{item.target_type}</td>
                          <td>{item.operator}</td>
                          <td>{item.assumption_value}</td>
                          <td>
                            {item.period_scope === "all_periods"
                              ? "All"
                              : `${formatDate(item.month_start)} → ${formatDate(
                                  item.month_end
                                )}`}
                          </td>

                          <td className="kp-actions">
                            <button
                              className="kp-btn kp-btn-soft"
                              onClick={() => setEditingAssumption(item)}
                            >
                              ✏️
                            </button>

                            <button
                              className="kp-btn kp-btn-soft"
                              onClick={() => deleteAssumption(item.id)}
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Comparison Filters</h3>
        </div>

        <div className="kp-form-grid kp-form-grid-4 kp-report-filters">
          <div className="kp-field">
            <label>Start Date</label>
            <input
              type="date"
              value={compareFilters.start_date}
              onChange={(e) =>
                setCompareFilters((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
            />
          </div>

          <div className="kp-field">
            <label>End Date</label>
            <input
              type="date"
              value={compareFilters.end_date}
              onChange={(e) =>
                setCompareFilters((prev) => ({
                  ...prev,
                  end_date: e.target.value,
                }))
              }
            />
          </div>

          <div className="kp-field">
            <label>Granularity</label>
            <select
              value={compareFilters.granularity}
              onChange={(e) =>
                setCompareFilters((prev) => ({
                  ...prev,
                  granularity: e.target.value,
                }))
              }
            >
              <option value="monthly">monthly</option>
              <option value="quarterly">quarterly</option>
              <option value="yearly">yearly</option>
              <option value="custom">custom</option>
            </select>
          </div>

          <div className="kp-field kp-field-align-end">
            <button
              type="button"
              className="kp-btn kp-btn-primary"
              onClick={loadScenarioResults}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      <PlanningScenarioMatrixTable
        title={
          selectedScenario
            ? `Scenario Matrix · ${selectedScenario.name}`
            : "Scenario Matrix"
        }
        rows={scenarioMatrixRows}
        collapsedGroups={collapsedGroups}
        onToggleGroup={toggleGroup}
      />

      <div className="kp-metric-row">
        <div className="kp-metric-card">
          <span>Base Total</span>
          <strong>{currency(totals.base)}</strong>
        </div>
        <div className="kp-metric-card">
          <span>Scenario Total</span>
          <strong>{currency(totals.scenario)}</strong>
        </div>
        <div className="kp-metric-card">
          <span>Variance</span>
          <strong>{currency(totals.variance)}</strong>
        </div>
      </div>

      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Detailed Comparison</h3>
        </div>

        <div className="kp-table-wrap">
          <table className="kp-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Account</th>
                <th>Type</th>
                <th>Base</th>
                <th>Scenario</th>
                <th>Variance</th>
                <th>Variance %</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan="7" className="kp-empty-cell">
                    No comparison results yet.
                  </td>
                </tr>
              ) : (
                results.map((row, index) => (
                  <tr key={`${row.account_id}-${row.period_key}-${index}`}>
                    <td>{row.period_label}</td>
                    <td>
                      {row.account_number} - {row.account_name}
                    </td>
                    <td>{row.account_type}</td>
                    <td>{currency(row.base_amount)}</td>
                    <td>{currency(row.scenario_amount)}</td>
                    <td>{currency(row.variance_amount)}</td>
                    <td>
                      {row.variance_pct === null
                        ? "N/A"
                        : `${row.variance_pct}%`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="kp-note">
        Create a scenario, add assumptions, recalculate it, then compare base vs
        scenario output in the matrix and detail table.
      </div>

      <CreateScenarioModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        companyId={companyId}
        sessionUserId={sessionUserId}
        selectedVersionId={selectedVersionId}
        forecastVersions={forecastVersions}
      />
      <EditAssumptionModal
        open={!!editingAssumption}
        assumption={editingAssumption}
        onClose={() => setEditingAssumption(null)}
        onSaved={async () => {
          await loadAssumptions();
          await loadScenarioResults();
        }}
      />
    </div>
  );
}
